import {isNil, isNotNil} from 'ramda';
import {isNonEmptyString} from 'ramda-adjunct';

import {useGetMakeModelWithMakeQuery} from '@dms/api/sales';
import i18n, {FALLBACK_LANGUAGE} from '@dms/i18n';

import {Nullish} from 'shared/web';

import {getVehicleLabels, VehicleLabels} from '../utils/getVehicleTitle';
import {useCustomTenantCatalogue} from './useCustomTenantCatalogue';

type Vehicle = {
  make?: string | null;
  vehicleType?: string;
  modelFamily?: string | null;
  trim?: string | null;
  variant?: string | null;
};

const EMPTY_LABELS: VehicleLabels = {
  make: undefined,
  modelFamily: undefined,
  trim: undefined,
  variant: undefined,
};

/**
 * Resolves vehicle display labels (make, modelFamily) from the make-model catalogue
 * and returns them together with trim/variant. Useful when callers need the individual
 * label parts rather than the concatenated title returned by `useGetVehicleTitle`.
 */
export function useGetVehicleLabels(
  vehicle: Vehicle | Nullish
): [VehicleLabels, {isLoading: boolean; isError: boolean}] {
  const {data, isLoading, isError} = useGetMakeModelWithMakeQuery(
    {
      vehicleType: vehicle?.vehicleType ?? '',
      make: vehicle?.make ?? '',
      lang: [i18n?.resolvedLanguage ?? FALLBACK_LANGUAGE],
    },
    {skip: isNil(vehicle?.vehicleType) || isNil(vehicle?.make)}
  );

  const make = vehicle?.make;

  const {
    customMakeOptions,
    customModelOptions,
    isLoading: isCustomTenantCatalogueLoading,
    isError: isCustomTenantCatalogueErrored,
  } = useCustomTenantCatalogue(
    vehicle?.vehicleType,
    isNotNil(make) && isNonEmptyString(make) ? [make] : null
  );

  return [
    isNotNil(vehicle)
      ? getVehicleLabels(vehicle, data, {
          makeOptions: customMakeOptions,
          modelOptions: customModelOptions,
        })
      : EMPTY_LABELS,
    {
      isLoading: isLoading || isCustomTenantCatalogueLoading,
      isError: isError || isCustomTenantCatalogueErrored,
    },
  ];
}
